import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import { format } from "timeago.js";
import { useNotifications } from "../../hooks/useNotifications";
import { navigateWithMobileDashboardScroll, primeMobileDashboardContentScroll } from "../../utils/navigateWithMobileDashboardScroll";
import { resolveNotificationNavigationPath } from "../../utils/notificationNavigation";

const NotificationBell = ({ isAdmin = false, variant = "dark" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Note: isAdmin prop is ignored - the hook determines admin status from route/context
  const {
    bellBadgeCount,
    acknowledgeBellView,
    notifications,
    markAsRead,
    lastSyncedAt,
  } = useNotifications();
  
  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/admin-");
  
  const isLight = variant === "light";
  const iconColor = isLight ? "#475569" : "rgb(255 255 255 / 83%)";
  const badgeClass = isLight
    ? "absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[#38513b] px-1 text-[10px] font-medium text-white"
    : "absolute right-0 top-0 rounded-full bg-[#CCBEA1] w-4 h-4 top right p-0 m-0 text-black font-mono text-[12px] leading-tight text-center";

  // Get latest 6 notifications from real-time notifications array
  // Ensure we're getting all notifications, sorted by date (newest first)
  const latestNotifications = useMemo(() => {
    const sorted = [...notifications].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA; // Newest first
    });
    const latest = sorted.slice(0, 6);
    return latest;
  }, [notifications, isAdminRoute]);

  // Close dropdown when clicking outside or when route changes
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  
  // Close dropdown when route changes (but use a ref to prevent reopening)
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      setIsOpen(false);
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  const handleNotificationClick = async (notification) => {
    // Close dropdown first
    setIsOpen(false);
    
    // For message notifications, don't mark as read here - let inbox page mark when conversation opens
    // For other notifications (orders, offers, etc.), mark as read when clicked
    if (!notification.read && notification.type !== "message_received") {
      console.log("🔔 Marking notification as read:", notification._id);
      try {
        await markAsRead(notification._id);
        console.log("✅ Notification marked as read");
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
    
    const type = String(notification?.type || "");
    const relatedId = notification?.relatedId?.toString?.() || notification?.relatedId;
    const isRefundFlow =
      type === "order_refund" ||
      type.includes("refund") ||
      type.includes("return");
    if (isAdminRoute && isRefundFlow && relatedId) {
      navigateWithMobileDashboardScroll(navigate, `/admin-order/${relatedId}`, { replace: false });
      return;
    }
    const path = resolveNotificationNavigationPath(notification, { isAdminRoute });
    if (path) {
      navigateWithMobileDashboardScroll(navigate, path, { replace: false });
    }
  };

  // Match cart/wishlist: badge always visible; opening the panel shows 0 until new unread arrive.
  const badgeNumber = isOpen ? 0 : bellBadgeCount;

  const getNotificationIcon = (type) => {
    switch (type) {
      case "order_placed":
      case "order_confirmed":
      case "order_shipped":
      case "order_delivered":
        return "📦";
      case "offer_received":
      case "offer_accepted":
      case "offer_rejected":
      case "offer_countered":
        return "💰";
      case "message_received":
        return "💬";
      default:
        return "🔔";
    }
  };

  return (
    <div 
      className="relative cursor-pointer mr-[15px]" 
        ref={dropdownRef}
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          if (next) acknowledgeBellView();
        }}
        aria-label="Notifications"
      >
        <IoMdNotificationsOutline size={30} color={iconColor} />
        <span className={badgeClass}>
          {badgeNumber > 99 ? "99+" : badgeNumber}
        </span>

        {isOpen && (
          <div 
            className="absolute w-80 rounded-lg border border-gray-200 bg-white shadow-xl z-[9999] flex flex-col overflow-hidden"
            style={{ 
              right: '0',
              top: 'calc(100% + 8px)',
              maxWidth: 'min(290px, calc(100vw - 1rem))',
              maxHeight: 'calc(100vh - 120px)',
              // Ensure it doesn't overflow - adjust right position if needed
              transform: 'translateX(0)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 bg-white sticky top-0 z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Last updated {lastSyncedAt ? format(lastSyncedAt) : "just now"}
                </p>
              </div>
              <Link
                to={isAdminRoute ? "/admin/notifications" : "/profile/notifications"}
                onClick={() => {
                  primeMobileDashboardContentScroll(
                    isAdminRoute ? "/admin/notifications" : "/profile/notifications"
                  );
                  setIsOpen(false);
                }}
                className="text-sm text-[#38513b] hover:underline"
              >
                View all
              </Link>
            </div>

            <div 
              className="overflow-y-auto flex-1 min-h-0"
              style={{
                maxHeight: '480px', // Fixed max height for scrollable area (6 notifications * ~80px each)
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 transparent',
              }}
            >
              {latestNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {latestNotifications.map((notification) => (
                    <button
                      key={notification._id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                        !notification.read ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl flex-shrink-0">
                          {getNotificationIcon(notification.type)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium ${
                              !notification.read ? "text-gray-900" : "text-gray-700"
                            }`}
                          >
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {format(notification.createdAt)}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default NotificationBell;
